import type { GameSettings } from '../core/types';

export interface AudioVolumes {
  master: number;
  effects: number;
  engine: number;
}

export interface EngineAudioState {
  speedMps: number;
  throttle: number;
}

export type CountdownStep = 3 | 2 | 1 | 'go' | 'GO';
export type AudioCue =
  | 'menu-select'
  | 'countdown-3'
  | 'countdown-2'
  | 'countdown-1'
  | 'race-start'
  | 'booster'
  | 'checkpoint'
  | 'finish'
  | 'tire-slide'
  | 'landing';

export interface AudioManagerOptions {
  contextFactory?: () => AudioContext | null;
  volumes?: Partial<AudioVolumes>;
}

interface AudioGraph {
  master: GainNode;
  effects: GainNode;
  engine: GainNode;
}

interface EngineVoice {
  primary: OscillatorNode;
  secondary: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
}

const MIN_GAIN = 0.0001;

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

function defaultContextFactory(): AudioContext | null {
  try {
    const browser = globalThis as typeof globalThis & {
      webkitAudioContext?: typeof AudioContext;
    };
    const Context = browser.AudioContext ?? browser.webkitAudioContext;
    return Context ? new Context() : null;
  } catch {
    return null;
  }
}

/**
 * Procedural WebAudio presentation for the whole game. No context is created
 * until unlock() runs from a user gesture; every other method is a safe no-op
 * while audio is locked or unsupported.
 */
export class AudioManager {
  private readonly contextFactory: () => AudioContext | null;
  private context: AudioContext | null = null;
  private graph: AudioGraph | null = null;
  private engineVoice: EngineVoice | null = null;
  private volumes: AudioVolumes;
  private gestureTarget: EventTarget | null = null;
  private readonly lastCueAt = new Map<string, number>();

  private readonly unlockFromGesture = () => {
    void this.unlock().then((ready) => {
      if (ready) {
        this.removeGestureUnlock();
      }
    });
  };

  constructor(options: AudioManagerOptions = {}) {
    this.contextFactory = options.contextFactory ?? defaultContextFactory;
    this.volumes = {
      master: clamp01(options.volumes?.master ?? 0.8),
      effects: clamp01(options.volumes?.effects ?? 0.85),
      engine: clamp01(options.volumes?.engine ?? 0.7),
    };
  }

  isReady(): boolean {
    return this.context?.state === 'running' && this.graph !== null;
  }

  async unlock(): Promise<boolean> {
    if (!this.context || this.context.state === 'closed') {
      this.context = this.contextFactory();
      this.graph = null;
      this.engineVoice = null;
    }

    const context = this.context;
    if (!context) {
      return false;
    }

    try {
      if (!this.graph) {
        this.graph = this.createGraph(context);
      }
      if (context.state === 'suspended') {
        await context.resume();
      }
      return context.state === 'running';
    } catch {
      return false;
    }
  }

  installGestureUnlock(
    target: EventTarget | null = typeof window === 'undefined' ? null : window,
  ): boolean {
    if (!target || this.gestureTarget === target) {
      return Boolean(target);
    }

    this.removeGestureUnlock();
    this.gestureTarget = target;
    target.addEventListener('pointerdown', this.unlockFromGesture);
    target.addEventListener('keydown', this.unlockFromGesture);
    target.addEventListener('touchstart', this.unlockFromGesture);
    return true;
  }

  removeGestureUnlock(): void {
    if (!this.gestureTarget) {
      return;
    }
    this.gestureTarget.removeEventListener('pointerdown', this.unlockFromGesture);
    this.gestureTarget.removeEventListener('keydown', this.unlockFromGesture);
    this.gestureTarget.removeEventListener('touchstart', this.unlockFromGesture);
    this.gestureTarget = null;
  }

  getVolumes(): AudioVolumes {
    return { ...this.volumes };
  }

  setVolumes(volumes: Partial<AudioVolumes>): void;
  setVolumes(master: number, effects?: number, engine?: number): void;
  setVolumes(
    volumesOrMaster: Partial<AudioVolumes> | number,
    effects?: number,
    engine?: number,
  ): void {
    const next =
      typeof volumesOrMaster === 'number'
        ? { master: volumesOrMaster, effects, engine }
        : volumesOrMaster;

    if (next.master !== undefined) {
      this.volumes.master = clamp01(next.master);
    }
    if (next.effects !== undefined) {
      this.volumes.effects = clamp01(next.effects);
    }
    if (next.engine !== undefined) {
      this.volumes.engine = clamp01(next.engine);
    }

    this.applyVolumes();
  }

  applySettings(
    settings: Pick<GameSettings, 'masterVolume' | 'effectsVolume' | 'engineVolume'>,
  ): void {
    this.setVolumes({
      master: settings.masterVolume,
      effects: settings.effectsVolume,
      engine: settings.engineVolume,
    });
  }

  setMasterVolume(volume: number): void {
    this.setVolumes({ master: volume });
  }

  setEffectsVolume(volume: number): void {
    this.setVolumes({ effects: volume });
  }

  setEngineVolume(volume: number): void {
    this.setVolumes({ engine: volume });
  }

  play(cue: AudioCue, intensity = 1): void {
    switch (cue) {
      case 'menu-select':
        this.playMenuSelect();
        break;
      case 'countdown-3':
        this.playCountdown(3);
        break;
      case 'countdown-2':
        this.playCountdown(2);
        break;
      case 'countdown-1':
        this.playCountdown(1);
        break;
      case 'race-start':
        this.playRaceStart();
        break;
      case 'booster':
        this.playBooster(intensity);
        break;
      case 'checkpoint':
        this.playCheckpoint();
        break;
      case 'finish':
        this.playFinish();
        break;
      case 'tire-slide':
        this.playTireSlide(intensity);
        break;
      case 'landing':
        this.playLanding(intensity);
        break;
    }
  }

  playMenuSelect(): void {
    this.withAudio((context, graph) => {
      this.tone(context, graph.effects, 620, 0.065, 0.12, 'triangle');
      this.tone(context, graph.effects, 920, 0.04, 0.06, 'sine', 0.035);
    });
  }

  playCountdown(step: CountdownStep): void {
    if (step === 'go' || step === 'GO') {
      this.playRaceStart();
      return;
    }

    const frequency = step === 3 ? 392 : step === 2 ? 440 : 523.25;
    this.withAudio((context, graph) => {
      this.tone(context, graph.effects, frequency, 0.14, 0.19, 'square');
      this.tone(context, graph.effects, frequency * 2, 0.08, 0.035, 'sine');
    });
  }

  playRaceStart(): void {
    this.withAudio((context, graph) => {
      this.tone(context, graph.effects, 523.25, 0.34, 0.15, 'square');
      this.tone(context, graph.effects, 659.25, 0.34, 0.12, 'triangle', 0.015);
      this.tone(context, graph.effects, 783.99, 0.4, 0.1, 'sine', 0.03);
    });
  }

  playBooster(intensity = 1): void {
    this.withAudio((context, graph) => {
      if (!this.canPlay('booster', context, 0.08)) {
        return;
      }
      const amount = clamp01(intensity);
      this.sweep(context, graph.effects, 150, 720, 0.42, 0.15 + amount * 0.09, 'sawtooth');
      this.noise(context, graph.effects, 0.34, 0.08 + amount * 0.12, 1800, 'bandpass');
    });
  }

  playCheckpoint(): void {
    this.withAudio((context, graph) => {
      this.tone(context, graph.effects, 880, 0.1, 0.11, 'sine');
      this.tone(context, graph.effects, 1174.66, 0.14, 0.12, 'triangle', 0.07);
    });
  }

  playFinish(): void {
    this.withAudio((context, graph) => {
      [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
        this.tone(context, graph.effects, frequency, 0.3, 0.11, 'triangle', index * 0.09);
      });
      this.tone(context, graph.effects, 261.63, 0.72, 0.1, 'sine', 0.02);
    });
  }

  playTireSlide(intensity = 1): void {
    this.withAudio((context, graph) => {
      if (!this.canPlay('tire-slide', context, 0.085)) {
        return;
      }
      const amount = clamp01(intensity);
      this.noise(context, graph.effects, 0.13, 0.025 + amount * 0.09, 900 + amount * 900, 'bandpass');
    });
  }

  playLanding(intensity = 1): void {
    this.withAudio((context, graph) => {
      if (!this.canPlay('landing', context, 0.1)) {
        return;
      }
      const amount = clamp01(intensity);
      this.sweep(context, graph.effects, 105, 42, 0.16, 0.09 + amount * 0.12, 'sine');
      this.noise(context, graph.effects, 0.1, 0.025 + amount * 0.07, 260, 'lowpass');
    });
  }

  updateEngine(state: EngineAudioState): void;
  updateEngine(speedMps: number, throttle?: number): void;
  updateEngine(stateOrSpeed: EngineAudioState | number, throttle = 0): void {
    this.withAudio((context, graph) => {
      const speedMps =
        typeof stateOrSpeed === 'number' ? stateOrSpeed : stateOrSpeed.speedMps;
      const throttleAmount =
        typeof stateOrSpeed === 'number' ? throttle : stateOrSpeed.throttle;
      const speed = clamp01(Math.abs(speedMps) / 92);
      const throttleLevel = clamp01(throttleAmount);
      const voice = this.engineVoice ?? this.createEngineVoice(context, graph.engine);
      this.engineVoice = voice;

      const fundamental = 52 + Math.pow(speed, 0.72) * 178 + throttleLevel * 24;
      const targetGain = 0.018 + speed * 0.105 + throttleLevel * 0.032;
      const now = context.currentTime;
      voice.primary.frequency.setTargetAtTime(fundamental, now, 0.045);
      voice.secondary.frequency.setTargetAtTime(fundamental * 2.01, now, 0.05);
      voice.filter.frequency.setTargetAtTime(420 + speed * 1850 + throttleLevel * 430, now, 0.06);
      voice.gain.gain.setTargetAtTime(targetGain, now, 0.055);
    });
  }

  stopEngine(fadeSeconds = 0.12): void {
    if (!this.context || !this.engineVoice) {
      return;
    }
    this.engineVoice.gain.gain.setTargetAtTime(
      MIN_GAIN,
      this.context.currentTime,
      Math.max(0.01, fadeSeconds / 3),
    );
  }

  async dispose(): Promise<void> {
    this.removeGestureUnlock();
    if (this.engineVoice) {
      try {
        this.engineVoice.primary.stop();
        this.engineVoice.secondary.stop();
      } catch {
        // Oscillators may already have stopped with the context.
      }
    }
    this.engineVoice = null;
    this.graph = null;

    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') {
      try {
        await context.close();
      } catch {
        // Context shutdown should not affect app teardown.
      }
    }
  }

  private createGraph(context: AudioContext): AudioGraph {
    const master = context.createGain();
    const effects = context.createGain();
    const engine = context.createGain();
    effects.connect(master);
    engine.connect(master);
    master.connect(context.destination);

    const graph = { master, effects, engine };
    this.graph = graph;
    this.applyVolumes();
    return graph;
  }

  private applyVolumes(): void {
    if (!this.context || !this.graph) {
      return;
    }
    const now = this.context.currentTime;
    this.graph.master.gain.setTargetAtTime(this.volumes.master, now, 0.02);
    this.graph.effects.gain.setTargetAtTime(this.volumes.effects, now, 0.02);
    this.graph.engine.gain.setTargetAtTime(this.volumes.engine, now, 0.02);
  }

  private withAudio(callback: (context: AudioContext, graph: AudioGraph) => void): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || context.state !== 'running') {
      return;
    }

    try {
      callback(context, graph);
    } catch {
      // Audio is presentation only; a browser audio failure must never break
      // vehicle input, timing, or a menu transition.
    }
  }

  private tone(
    context: AudioContext,
    destination: AudioNode,
    frequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    const startAt = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startAt);
    envelope.gain.setValueAtTime(MIN_GAIN, startAt);
    envelope.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, volume), startAt + 0.008);
    envelope.gain.exponentialRampToValueAtTime(MIN_GAIN, startAt + duration);
    oscillator.connect(envelope);
    envelope.connect(destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + duration + 0.025);
  }

  private sweep(
    context: AudioContext,
    destination: AudioNode,
    fromFrequency: number,
    toFrequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
  ): void {
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(Math.max(1, fromFrequency), now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, toFrequency), now + duration);
    envelope.gain.setValueAtTime(MIN_GAIN, now);
    envelope.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, volume), now + 0.02);
    envelope.gain.exponentialRampToValueAtTime(MIN_GAIN, now + duration);
    oscillator.connect(envelope);
    envelope.connect(destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.025);
  }

  private noise(
    context: AudioContext,
    destination: AudioNode,
    duration: number,
    volume: number,
    frequency: number,
    filterType: BiquadFilterType,
  ): void {
    const length = Math.max(1, Math.ceil(context.sampleRate * duration));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < channel.length; index += 1) {
      channel[index] = Math.random() * 2 - 1;
    }

    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const envelope = context.createGain();
    source.buffer = buffer;
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = filterType === 'bandpass' ? 1.2 : 0.7;
    const now = context.currentTime;
    envelope.gain.setValueAtTime(Math.max(MIN_GAIN, volume), now);
    envelope.gain.exponentialRampToValueAtTime(MIN_GAIN, now + duration);
    source.connect(filter);
    filter.connect(envelope);
    envelope.connect(destination);
    source.start(now);
    source.stop(now + duration + 0.02);
  }

  private createEngineVoice(context: AudioContext, destination: AudioNode): EngineVoice {
    const primary = context.createOscillator();
    const secondary = context.createOscillator();
    const primaryGain = context.createGain();
    const secondaryGain = context.createGain();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    primary.type = 'sawtooth';
    secondary.type = 'square';
    primaryGain.gain.value = 0.72;
    secondaryGain.gain.value = 0.14;
    filter.type = 'lowpass';
    filter.frequency.value = 500;
    filter.Q.value = 1.1;
    gain.gain.value = MIN_GAIN;
    primary.connect(primaryGain);
    secondary.connect(secondaryGain);
    primaryGain.connect(filter);
    secondaryGain.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    primary.start();
    secondary.start();
    return { primary, secondary, filter, gain };
  }

  private canPlay(key: string, context: AudioContext, minimumInterval: number): boolean {
    const lastTime = this.lastCueAt.get(key) ?? Number.NEGATIVE_INFINITY;
    if (context.currentTime - lastTime < minimumInterval) {
      return false;
    }
    this.lastCueAt.set(key, context.currentTime);
    return true;
  }
}

export const audioManager = new AudioManager();

